import type {
  CreateShareLinkInput,
  GuestFilesResult,
  ListShareLinksResult,
  RequestUploadUrlOutput,
  ShareLink,
  ShareLinkAccessInput,
  ShareLinkAccessOutput,
  ShareLinkPublicMeta,
  StreamTokenOutput,
} from '@terafile/shared'
import { apiFetch } from './api.js'

/**
 * 共有URL（オーナー側 + ゲスト側）の API クライアント。
 *
 * - オーナー側は `apiFetch` を経由（Bearer JWT 自動付与）
 * - ゲスト側は別途 GUEST トークンを Authorization で渡すため fetch を直に使う
 */

// ----- オーナー側 -----

export async function createShareLink(
  projectId: string,
  input: CreateShareLinkInput,
): Promise<ShareLink> {
  return apiFetch<ShareLink>(`/projects/${projectId}/share-links`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function listShareLinks(projectId: string): Promise<ListShareLinksResult> {
  return apiFetch<ListShareLinksResult>(`/projects/${projectId}/share-links`)
}

export async function revokeShareLink(id: string): Promise<ShareLink> {
  return apiFetch<ShareLink>(`/share-links/${id}/revoke`, { method: 'POST' })
}

// ----- ゲスト側 -----

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as {
    success?: boolean
    data?: T
    error?: { code?: string; message?: string }
  } | null
  if (!res.ok) {
    const err = body?.error
    throw new GuestApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `HTTP ${res.status}`,
    )
  }
  return (body?.data ?? null) as T
}

export class GuestApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'GuestApiError'
  }
}

export async function getShareLinkMeta(token: string): Promise<ShareLinkPublicMeta> {
  return unwrap<ShareLinkPublicMeta>(await fetch(`${API_BASE}/share/${token}`))
}

export async function accessShareLink(
  token: string,
  input: ShareLinkAccessInput,
): Promise<ShareLinkAccessOutput> {
  return unwrap<ShareLinkAccessOutput>(
    await fetch(`${API_BASE}/share/${token}/access`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function listGuestFiles(token: string, guestToken: string): Promise<GuestFilesResult> {
  return unwrap<GuestFilesResult>(
    await fetch(`${API_BASE}/share/${token}/files`, {
      headers: { authorization: `Bearer ${guestToken}` },
    }),
  )
}

export async function getGuestStreamToken(
  token: string,
  fileId: string,
  guestToken: string,
): Promise<StreamTokenOutput> {
  return unwrap<StreamTokenOutput>(
    await fetch(`${API_BASE}/share/${token}/files/${fileId}/stream-token`, {
      method: 'POST',
      headers: { authorization: `Bearer ${guestToken}` },
    }),
  )
}

export async function getGuestDownloadUrl(
  token: string,
  fileId: string,
  guestToken: string,
): Promise<{ url: string; expiresAt: string }> {
  return unwrap(
    await fetch(`${API_BASE}/share/${token}/files/${fileId}/download-url`, {
      headers: { authorization: `Bearer ${guestToken}` },
    }),
  )
}

/**
 * ゲスト用画像/PDF プレビュー署名URL。VIEW 権限でも使える（inline 表示）。
 */
export async function getGuestPreviewUrl(
  token: string,
  fileId: string,
  guestToken: string,
): Promise<{ url: string; expiresAt: string }> {
  return unwrap(
    await fetch(`${API_BASE}/share/${token}/files/${fileId}/preview-url`, {
      headers: { authorization: `Bearer ${guestToken}` },
    }),
  )
}

export async function requestGuestUploadUrl(
  token: string,
  guestToken: string,
  input: { originalName: string; sizeBytes: number; mimeType: string },
): Promise<RequestUploadUrlOutput> {
  return unwrap<RequestUploadUrlOutput>(
    await fetch(`${API_BASE}/share/${token}/upload-url`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${guestToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(input),
    }),
  )
}

export async function postGuestPlaybackEvent(
  token: string,
  fileId: string,
  guestToken: string,
  payload: { event: 'progress' | 'end' | 'seek'; playPositionSec: number },
): Promise<void> {
  await fetch(`${API_BASE}/share/${token}/files/${fileId}/playback-events`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${guestToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(payload),
  }).catch(() => {
    // 再生イベントの欠落は致命的ではない
  })
}
